import { useEffect, useRef, useState } from 'react'
import { getProgressColor } from '@/lib/constants'

interface ProgressBarProps {
  value:       number            // 0–100
  max?:        number            // default 100
  label?:      string
  showLabel?:  boolean
  showPercent?: boolean
  size?:       'sm' | 'md' | 'lg'
  animated?:   boolean
  color?:      string            // override auto color
  className?:  string
  validatedValue?: number       // show validated progress as overlay
}

export default function ProgressBar({
  value,
  max = 100,
  label,
  showLabel  = false,
  showPercent = true,
  size       = 'md',
  animated   = true,
  color,
  className  = '',
  validatedValue,
}: ProgressBarProps) {
  const [displayValue, setDisplayValue] = useState(animated ? 0 : value)
  const animRef                         = useRef<number | null>(null)

  const pct          = Math.min(100, Math.max(0, (value / max) * 100))
  const validatedPct = validatedValue !== undefined
    ? Math.min(100, Math.max(0, (validatedValue / max) * 100))
    : null

  const barColor     = color ?? getProgressColor(pct)

  useEffect(() => {
    if (!animated) {
      setDisplayValue(pct)
      return
    }

    // Animate from 0 to target
    const startTime = performance.now()
    const duration  = 800

    const tick = (now: number) => {
      const elapsed  = now - startTime
      const progress = Math.min(elapsed / duration, 1)
      // Ease out cubic
      const eased    = 1 - Math.pow(1 - progress, 3)
      setDisplayValue(eased * pct)

      if (progress < 1) {
        animRef.current = requestAnimationFrame(tick)
      }
    }

    animRef.current = requestAnimationFrame(tick)
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current)
    }
  }, [pct, animated])

  const heightClass = {
    sm: 'h-1.5',
    md: 'h-2.5',
    lg: 'h-4',
  }[size]

  const textSize = {
    sm: 'text-xs',
    md: 'text-xs',
    lg: 'text-sm',
  }[size]

  return (
    <div className={`w-full ${className}`}>
      {/* Label row */}
      {(showLabel || showPercent) && (
        <div className="flex justify-between items-center mb-1">
          {showLabel && label && (
            <span className={`${textSize} text-gray-600 font-medium truncate`}>{label}</span>
          )}
          {showPercent && (
            <span className={`${textSize} font-semibold ml-auto`} style={{ color: barColor }}>
              {pct.toFixed(1)}%
            </span>
          )}
        </div>
      )}

      {/* Bar */}
      <div className={`relative w-full bg-gray-100 rounded-full overflow-hidden ${heightClass}`}>
        {/* Validated progress (darker, behind main bar) */}
        {validatedPct !== null && validatedPct > 0 && (
          <div
            className="absolute inset-y-0 left-0 rounded-full"
            style={{
              width:      `${validatedPct}%`,
              backgroundColor: 'rgba(0,0,0,0.15)',
            }}
          />
        )}

        {/* Reported progress */}
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-colors duration-500"
          style={{
            width:           `${displayValue}%`,
            backgroundColor: barColor,
          }}
        />

        {/* Shimmer effect when in progress */}
        {pct > 0 && pct < 100 && (
          <div
            className="absolute inset-y-0 rounded-full overflow-hidden"
            style={{ left: 0, width: `${displayValue}%` }}
          >
            <div
              className="absolute inset-0 opacity-30"
              style={{
                background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
                animation: 'shimmer 2s infinite',
              }}
            />
          </div>
        )}
      </div>

      {/* Validated label */}
      {validatedPct !== null && size === 'lg' && (
        <div className="flex justify-between mt-1">
          <span className="text-xs text-gray-400">Reportado: {pct.toFixed(1)}%</span>
          <span className="text-xs text-gray-400">Validado: {validatedPct.toFixed(1)}%</span>
        </div>
      )}
    </div>
  )
}

// =============================================================================
// Mini progress indicator (used in tables)
// =============================================================================

export function ProgressCell({ value, className = '' }: { value: number; className?: string }) {
  const color = getProgressColor(value)
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${value}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-xs font-medium w-10 text-right" style={{ color }}>
        {value.toFixed(0)}%
      </span>
    </div>
  )
}
